import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form, Input, Spin,
} from 'antd';
import { AxiosError } from 'axios';
import {
    forwardRef, useEffect, useImperativeHandle, useMemo, useRef,
} from 'react';
import {
    Controller, useForm,
} from 'react-hook-form';
import { type To } from 'react-router-dom';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';

import { projectsMutations } from '@/api';
import { Project } from '@/types';

import {
    FormPrimaryActionsRow, FormSaveFloatButton,
} from './FormSubmitChrome';
import { TextEditor } from './TextEditor';

const PROJECT_FORM_DOM_ID = 'project-form';

const projectSchema = z.object({
    title: z.string().min(1, 'Назва обов\'язкова').max(255, 'Максимум 255 символів').trim(),
    description: z.union([z.string().max(10000, 'Максимум 10000 символів'), z.null()]).optional(),
    comment: z.union([z.string().max(10000, 'Максимум 10000 символів'), z.null()]).optional(),
    regulation: z.union([z.string().max(10000, 'Максимум 10000 символів'), z.null()]).optional(),
    url: z.union([
        z.string().url('Має бути валідна URL адреса').max(2048, 'Максимум 2048 символів').trim(),
        z.string().length(0),
        z.null(),
    ]).optional().transform((val) => (val === '' ? null : val)),
});

type ProjectFormData = z.output<typeof projectSchema>;

type ProjectFormProps = {
    initData?: Project | null;
    onSubmit: (data: ProjectFormData) => void;
    onCancel?: () => void;
    loading?: boolean;
    cancelButtonText?: string;
    cancelTo?: To;
};

export type ProjectFormRef = {
    reset: () => void;
};

export const ProjectForm = forwardRef<ProjectFormRef, ProjectFormProps>(({
    initData,
    onSubmit,
    onCancel,
    loading = false,
    cancelButtonText,
    cancelTo,
}, ref) => {
    const hiddenSubmitRef = useRef<HTMLButtonElement>(null);

    const defaultValues = useMemo<ProjectFormData>(() => ({
        title: initData?.title || '',
        description: initData?.description || null,
        comment: initData?.comment || null,
        regulation: initData?.regulation || null,
        url: initData?.url || null,
    }), [initData]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<ProjectFormData>({
        resolver: zodResolver(projectSchema) as any,
        defaultValues,
        mode: 'onChange',
    });

    useEffect(() => {
        if (initData) {
            const newDefaultValues = {
                title: initData.title || '',
                description: initData.description || null,
                comment: initData.comment || null,
                regulation: initData.regulation || null,
                url: initData.url || null,
            };
            reset(newDefaultValues, { keepDirty: false });
        }
    }, [initData, reset]);

    useImperativeHandle(ref, () => ({
        reset: () => {
            reset(defaultValues);
        },
    }), [defaultValues, reset]);

    const handleFormSubmit = (data: ProjectFormData) => {
        onSubmit(data);
    };

    const { mutateAsync: uploadWysiwygImage } = useMutation(
        projectsMutations.uploadWysiwygImage(initData?.id || ''),
    );

    const handleProjectImageUpload = async (file: File): Promise<{ url: string }> => {
        const response = await uploadWysiwygImage(file);
        return { url: response.url };
    };

    const primaryDisabled = loading || (!!initData && !isDirty);
    const submitLabel = initData ? 'Оновити' : 'Створити';

    return (
        <Spin spinning={loading}>
            <Form
                id={PROJECT_FORM_DOM_ID}
                layout="vertical"
                onFinish={handleSubmit(handleFormSubmit)}
            >
                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginBottom: 24 }}
                />
                <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Назва"
                            required
                            validateStatus={errors.title ? 'error' : ''}
                            help={errors.title?.message}
                        >
                            <Input
                                {...field}
                                placeholder="Введіть назву проекту"
                                maxLength={255}
                                showCount
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Опис"
                            validateStatus={errors.description ? 'error' : ''}
                            help={errors.description?.message}
                        >
                            <Input.TextArea
                                {...field}
                                value={field.value || ''}
                                placeholder="Введіть опис проекту"
                                maxLength={10000}
                                showCount
                                rows={4}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="comment"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Коментар"
                            validateStatus={errors.comment ? 'error' : ''}
                            help={errors.comment?.message}
                        >
                            <Input.TextArea
                                {...field}
                                value={field.value || ''}
                                placeholder="Введіть коментар"
                                maxLength={10000}
                                showCount
                                rows={4}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="regulation"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Регламент"
                            validateStatus={errors.regulation ? 'error' : ''}
                            help={errors.regulation?.message}
                        >
                            {/*<TextEditorWarning />*/}
                            <TextEditor
                                value={field.value || ''}
                                onChange={field.onChange}
                                disabled={loading}
                                onImageUpload={initData?.id ? handleProjectImageUpload : undefined}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="url"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="URL"
                            validateStatus={errors.url ? 'error' : ''}
                            help={errors.url?.message}
                        >
                            <Input
                                {...field}
                                value={field.value || ''}
                                placeholder="Введіть URL адресу"
                                maxLength={2048}
                                showCount
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginTop: 24 }}
                />
            </Form>
            <FormSaveFloatButton
                formDomId={PROJECT_FORM_DOM_ID}
                hiddenSubmitRef={hiddenSubmitRef}
                loading={loading}
                submitDisabled={primaryDisabled}
                submitLabel={submitLabel}
            />
        </Spin>
    );
});
